/**
 * Chat Context - State Management for Inventory Chat
 *
 * Provides global state for the chat widget including:
 * - Open/closed state
 * - Current session
 * - Message history
 * - Keyboard shortcuts (Cmd+K)
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

// Types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls?: Array<{
    toolName: string;
    args: Record<string, unknown>;
    result?: unknown;
  }>;
  createdAt: Date;
}

export interface SuggestedAction {
  label: string;
  action: string;
  params?: Record<string, unknown>;
}

interface ChatState {
  isOpen: boolean;
  sessionId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  suggestedActions: SuggestedAction[];
}

type ChatAction =
  | { type: 'OPEN_CHAT' }
  | { type: 'CLOSE_CHAT' }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'SET_SESSION'; sessionId: string }
  | { type: 'CLEAR_SESSION' }
  | { type: 'ADD_MESSAGE'; message: ChatMessage }
  | { type: 'SET_MESSAGES'; messages: ChatMessage[] }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SUGGESTED_ACTIONS'; actions: SuggestedAction[] };

interface ChatContextValue extends ChatState {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  setSession: (sessionId: string) => void;
  clearSession: () => void;
  addMessage: (message: ChatMessage) => void;
  setMessages: (messages: ChatMessage[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuggestedActions: (actions: SuggestedAction[]) => void;
}

const initialState: ChatState = {
  isOpen: false,
  sessionId: null,
  messages: [],
  isLoading: false,
  error: null,
  suggestedActions: [],
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'OPEN_CHAT':
      return { ...state, isOpen: true };
    case 'CLOSE_CHAT':
      return { ...state, isOpen: false };
    case 'TOGGLE_CHAT':
      return { ...state, isOpen: !state.isOpen };
    case 'SET_SESSION':
      return { ...state, sessionId: action.sessionId };
    case 'CLEAR_SESSION':
      return { ...state, sessionId: null, messages: [], suggestedActions: [] };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_SUGGESTED_ACTIONS':
      return { ...state, suggestedActions: action.actions };
    default:
      return state;
  }
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const openChat = useCallback(() => dispatch({ type: 'OPEN_CHAT' }), []);
  const closeChat = useCallback(() => dispatch({ type: 'CLOSE_CHAT' }), []);
  const toggleChat = useCallback(() => dispatch({ type: 'TOGGLE_CHAT' }), []);
  const setSession = useCallback((sessionId: string) => dispatch({ type: 'SET_SESSION', sessionId }), []);
  const clearSession = useCallback(() => dispatch({ type: 'CLEAR_SESSION' }), []);
  const addMessage = useCallback((message: ChatMessage) => dispatch({ type: 'ADD_MESSAGE', message }), []);
  const setMessages = useCallback((messages: ChatMessage[]) => dispatch({ type: 'SET_MESSAGES', messages }), []);
  const setLoading = useCallback((isLoading: boolean) => dispatch({ type: 'SET_LOADING', isLoading }), []);
  const setError = useCallback((error: string | null) => dispatch({ type: 'SET_ERROR', error }), []);
  const setSuggestedActions = useCallback((actions: SuggestedAction[]) => dispatch({ type: 'SET_SUGGESTED_ACTIONS', actions }), []);

  // Keyboard shortcut: Cmd+K or Ctrl+K to toggle chat
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        toggleChat();
      }
      // Escape to close
      if (event.key === 'Escape' && state.isOpen) {
        closeChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat, closeChat, state.isOpen]);

  const value: ChatContextValue = {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    setSession,
    clearSession,
    addMessage,
    setMessages,
    setLoading,
    setError,
    setSuggestedActions,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

export default ChatContext;
